import type { AccessContext, TaDeskCapability } from "@sdk/access";
import { hasAnyCapability } from "@sdk/access/resolve-access-context";

export interface RouteAccessRule {
  path: string;
  match: "exact" | "prefix";
  anyCapability?: TaDeskCapability[];
  salesAccess?: boolean;
  redirectTo?: string;
}

export const ROUTE_ACCESS_RULES: RouteAccessRule[] = [
  { path: "/dashboard", match: "exact" },
  { path: "/my/customers", match: "exact", salesAccess: true },
  {
    path: "/my/enquiries",
    match: "exact",
    anyCapability: ["MANAGE_ENQUIRIES"],
  },
  { path: "/my/quotes", match: "exact", anyCapability: ["MANAGE_QUOTES"] },
  {
    path: "/my/bookings",
    match: "exact",
    anyCapability: ["MANAGE_BOOKINGS"],
  },
  {
    path: "/my/performance",
    match: "exact",
    anyCapability: ["MANAGE_ENQUIRIES", "MANAGE_QUOTES", "MANAGE_BOOKINGS"],
  },
  { path: "/customers", match: "exact" },
  { path: "/customer-credit-vouchers", match: "exact" },
  {
    path: "/enquiries",
    match: "exact",
    anyCapability: ["VIEW_TEAM_ENQUIRIES"],
  },
  { path: "/quotes", match: "exact", anyCapability: ["VIEW_TEAM_QUOTES"] },
  {
    path: "/quote-templates",
    match: "exact",
    anyCapability: ["MANAGE_QUOTE_TEMPLATES"],
  },
  {
    path: "/bookings",
    match: "exact",
    anyCapability: ["VIEW_ALL_BOOKINGS", "VIEW_TEAM_BOOKINGS"],
  },
  {
    path: "/bookings/",
    match: "prefix",
    anyCapability: [
      "MANAGE_BOOKINGS",
      "VIEW_ALL_BOOKINGS",
      "VIEW_TEAM_BOOKINGS",
      "MANAGE_BOOKING_FINANCE",
    ],
  },
  {
    path: "/quotes/",
    match: "prefix",
    anyCapability: ["MANAGE_QUOTES", "VIEW_TEAM_QUOTES"],
  },
  { path: "/reports/", match: "prefix", anyCapability: ["VIEW_REPORTS"] },
  { path: "/accounting/", match: "prefix", anyCapability: ["VIEW_ACCOUNTING"] },
  { path: "/finance/", match: "prefix", anyCapability: ["VIEW_ACCOUNTING"] },
  { path: "/ledger-transactions", match: "exact", anyCapability: ["VIEW_ACCOUNTING"] },
];

function ruleMatchesPath(rule: RouteAccessRule, pathname: string): boolean {
  if (rule.match === "exact") {
    return pathname === rule.path;
  }
  return pathname.startsWith(rule.path);
}

export function findRouteAccessRule(pathname: string): RouteAccessRule | null {
  const matches = ROUTE_ACCESS_RULES.filter((rule) =>
    ruleMatchesPath(rule, pathname)
  );
  if (!matches.length) return null;
  return matches.sort((a, b) => b.path.length - a.path.length)[0];
}

export function canAccessRoute(
  pathname: string,
  ctx: AccessContext | null
): boolean {
  const rule = findRouteAccessRule(pathname);
  if (!rule) return true;
  if (!ctx) return false;
  if (ctx.isOwner) return true;

  if (rule.salesAccess) {
    return hasAnyCapability(ctx, [
      "MANAGE_CONTACTS",
      "MANAGE_ENQUIRIES",
      "MANAGE_QUOTES",
      "MANAGE_BOOKINGS",
    ]);
  }

  if (!rule.anyCapability?.length) {
    return true;
  }

  return hasAnyCapability(ctx, rule.anyCapability);
}
